<template>
    <app-layout title="User Management">
        <template v-slot:header>
            <div class="flex justify-between">
                <h2 class="font-bold text-4xl dark:text-gray-200">
                    User Management
                </h2>
                <inertia-link :href="route('admin.users.create')" class="">
                    <jet-button :has-icon="true">Create user</jet-button>
                </inertia-link>
            </div>
        </template>
        <div class="w-full overflow-hidden rounded-lg shadow">
            <table-layout>
                <template v-slot:theadRow>
                    <th class="px-4 py-3">Name</th>
                    <th class="px-4 py-3">Email</th>
                    <th class="px-4 py-3">Roles</th>
                    <th class="px-4 py-3">Created at</th>
                    <th class="px-4 py-3">Status</th>
                    <th class="px-4 py-3">Actions</th>
                </template>
                <template v-slot:tbody>
                    <tr v-for="user in paginatedUsers.data" class="text-gray-700 dark:text-gray-400">
                        <td class="px-4 py-3">
                            <div class="flex items-center text-sm">
                                <!-- Avatar with inset shadow -->
                                <div
                                    class="relative hidden w-8 h-8 mr-3 rounded-full md:block"
                                >
                                    <img
                                        class="object-cover w-full h-full rounded-full"
                                        :src="user.profile_photo_url"
                                        alt=""
                                        loading="lazy"
                                    />
                                    <div
                                        class="absolute inset-0 rounded-full shadow-inner"
                                        aria-hidden="true"
                                    ></div>
                                </div>
                                <div>
                                    <p class="font-semibold">{{ user.name }}</p>
                                    <p class="text-xs text-gray-600 dark:text-gray-400">
                                        {{ user.tenant ? user.tenant.name : '' }}
                                    </p>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ user.email }}
                        </td>
                        <td class="px-4 py-3 text-sm">
                            <template v-for="role in user.roles">
                                {{ role.name }}<br/>
                            </template>
                        </td>
                        <td class="px-4 py-3 text-sm">
                            {{ moment(user.created_at).format("DD-MM-YYYY HH:mm") }}
                        </td>
                        <td class="px-4 py-3 text-xs">
                            <pill :type="user.active ? 'success' : 'danger'">
                                {{ user.active ? 'Active' : 'Inactive' }}
                            </pill>
                            <pill class="ml-2" :type="user.email_verified_at ? 'success' : 'warning'">
                                {{ user.email_verified_at ? 'Email verified' : 'Pending email verification' }}
                            </pill>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex items-center space-x-1 text-sm" v-if="$page.props.user.id !== user.id">
                                <inertia-link :href="route('admin.survey.index', {orgId: user.tenant_id})" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                              aria-label="View Surveys" title="View Surveys"
                                >
                                    <svg class="w-5 h-5" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                                        <path d="M384 112v352c0 26.51-21.49 48-48 48H48c-26.51 0-48-21.49-48-48V112c0-26.51 21.49-48 48-48h80c0-35.29 28.71-64 64-64s64 28.71 64 64h80c26.51 0 48 21.49 48 48zM192 40c-13.255 0-24 10.745-24 24s10.745 24 24 24 24-10.745 24-24-10.745-24-24-24m96 114v-20a6 6 0 0 0-6-6H102a6 6 0 0 0-6 6v20a6 6 0 0 0 6 6h180a6 6 0 0 0 6-6z"></path>
                                    </svg>
                                </inertia-link>
                                <inertia-link :href="route('admin.users.edit', user.id)" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Edit" title="Edit"
                                >
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                    </svg>
                                </inertia-link>
                                <button @click.prevent="deleteUser(user)" class="flex items-center justify-between px-2 py-2 text-sm font-medium leading-5 text-purple-600 rounded-lg dark:text-gray-400 focus:outline-none focus:shadow-outline-gray"
                                        aria-label="Delete" title="Delete"
                                >
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                                    </svg>
                                </button>
                            </div>
                        </td>
                    </tr>
                </template>
            </table-layout>
            <div class="p-3 bg-gray-50">
                <table-pagination :pagination-object="paginatedUsers"/>
            </div>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import JetButton from "@/Jetstream/Button";
import TablePagination from "@/Jetstream/TablePagination";
import TableLayout from "@/Jetstream/TableLayout";
import Pill from "@/Jetstream/Pill";
import { Inertia } from '@inertiajs/inertia';
import { inject } from 'vue'

export default defineComponent({
    components: {
        JetButton,
        Pill,
        TableLayout,
        TablePagination,
        AppLayout
    },
    props: {
        paginatedUsers: Object,
    },
    data() {
        return {
            title: "Test"
        }
    },
    setup() {
        const $swal = inject('$swal');

        const deleteUser = (user) => {
            $swal({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.delete(route('admin.users.destroy', user.id));
                }
            });
        }
        return {
            deleteUser,
        };
    }
})
</script>
